/**
 * Common ProductRailHorizontal component used by other modules as a reusable component which returns the ui for a product carousel.
 *
 * @module views/Organisms/ProductRailHorizontal
 * @memberof -Common
 */
import './ProductRailHorizontal.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Spotlight as PalSpotlight } from 'web-palette/dist/components/Spotlight/Spotlight';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Image from '@ulta/core/components/Image/Image';
import Loader from '@ulta/core/components/Loader/Loader';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils, { manageRailLoading } from '@ulta/utils/carouselUtils/carouselUtils';

import SpotlightCard from '../SpotlightCard/SpotlightCard';

/**
 * Represents a ProductRailHorizontal component
 *
 * @method
 * @param { ProductRailHorizontalProps} props - React properties passed from composition
 * @returns ProductRailHorizontal
 */
export const ProductRailHorizontal = function( props ){
  const productRailHorizontalRef = useRef( null );

  const { breakpoint } = useDeviceInflection();

  const {
    componentKey,
    dataCapture,
    items,
    setShowSpacer,
    spotlight,

    /* Rail Title */
    title = defaultProps.title,
    subtitle,
    viewAllAction,

    /* Loader */
    enableLoader = defaultProps.enableLoader,
    loading,

    /* Rail meta data */
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    countLabel = defaultProps.countLabel,
    listPriceAccessibilityLabel,
    listPriceRangeAccessibilityLabel,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction,
    reviewAccessibilityLabel,
    salePriceAccessibilityLabel,
    salePriceRangeAccessibilityLabel
  } = props;

  const isHighlighter = !!(
    spotlight &&
    spotlight.eyebrow &&
    spotlight.title &&
    spotlight.subtitle &&
    spotlight.graphicElement?.graphicImage?.imageUrl
  );

  /* Checks if Rail is in a small device */
  let isSmallDevice = breakpoint.isSmallDevice();

  const isHighlighterRail = ( isHighlighter && isSmallDevice );
  const isHighlighterRailDesktop = ( isHighlighter && !isSmallDevice );

  /* Setting variables */
  let formattedGraphicElement;
  let formattedSpotlightCard = null;
  const loaderArraySize = isHighlighterRail ? 1 : 4;
  const productCards = isHighlighterRail ? items?.slice( 1 ) : items ?? [];

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  const [loadingHasBeenSeenTrue, setLoadingHasBeenSeenTrue] = useState( false );
  const [showLoader, setShowLoader] = useState( !!enableLoader );
  /* TODO: showHeader needs to be added to palette Rail to work */
  const [showHeader, setShowHeader] = useState( !enableLoader );

  useEffect( () => {
    manageRailLoading( { items: productCards, loading, loadingHasBeenSeenTrue }, { setShowLoader, setShowHeader } );
  }, [productCards, loading] );

  useEffect( ()=>{
    if( productCards?.length === 0 ){
      setShowSpacer( false );
    }
    else {
      setShowSpacer( true );
    }
  }, [productCards] );

  useEffect( () => {
    if( loading && !loadingHasBeenSeenTrue ){
      setLoadingHasBeenSeenTrue( true );
    }
  }, [loading, loadingHasBeenSeenTrue] );

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    countLabel: hasItems( productCards ) ? countLabel : '',
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  // if we need the metaData for SEO or DC purposes, we will have to pass this in as an external media object. In that case, palette Spotlight will need a small update to accept an externalMediaElement
  if( isHighlighter ){
    formattedGraphicElement = (
      <Image
        src={ spotlight.graphicElement.graphicImage.imageUrl }
        metaData={ spotlight.graphicElement.graphicImage.metaData }
        alt='decorative'
      />
    );
    formattedSpotlightCard = (
      <SpotlightCard
        ref={ productRailHorizontalRef }
        formattedGraphicElement={ formattedGraphicElement }
        spotlight={ spotlight }
        product={ items[0] }
      />
    );
  }

  if( !showLoader && !hasItems( productCards ) ){
    setShowSpacer( false );
    return null;
  }

  return (
    <div
      className={
        classNames( 'ProductRailHorizontal', {
          [`ProductRailHorizontal--hasSpotlight`]: isHighlighterRailDesktop
        } )
      }
      ref={ productRailHorizontalRef }
    >
      <GridContainer>
        <Rail
          railRef={ productRailHorizontalRef }
          id={ railUuid }
          items={ productCards }
          previousClickAction={ previousClickAction }
          nextClickAction={ nextClickAction }
          headline={ title }
          headlineTag='h2'
          showCountLabel={ true }
          subheadline={ subtitle }
          viewAllAction={ viewAllAction }
          isHighlighter={ isHighlighter }
          externalElementMobileSpotlightCard={ formattedSpotlightCard }
          externalSmallDevice={ isSmallDevice }
          spotlight={ spotlight }
          dataCapture={ dataCapture }
          _railADAmeta={ _railADAmeta }
          _railMeta={ _railMeta }
          sliderPerViewXL={ 4 }
          slidesPerGroupXL={ 4 }
          sliderPerViewLG={ 4 }
          sliderHighlightPerViewLG={ 3 }
          slidesPerGroupLG={ 4 }
          slidesHighlightPerGroupLG={ 3 }
          sliderPerViewMD={ 3 }
          slidesPerGroupMD={ 3 }
          slidesPerGroupSM={ 1 }
          sliderPerViewSM={ 1.5 }
        >
          { isHighlighterRailDesktop &&
            <Slide>
              <PalSpotlight
                eyebrow={ spotlight.eyebrow }
                title={ spotlight.title }
                subtitle={ spotlight.subtitle }
                externalGraphicImage={ formattedGraphicElement }
                textAlign='right'
              />
            </Slide>
          }
          { productCards?.length > 0 &&
            productCards.map( ( product, index ) => {
              return (
                <Slide key={ index }>
                  <div key={ product.skuId + '_' + index }>
                    <AsyncComponent
                      moduleName='ProductCard'
                      carouselIndex={ index }
                      componentKey={ componentKey }
                      // TODO: this componentLastUpdated needs to be refactored -- https://ulta.atlassian.net/browse/DSOTF2-109266
                      componentLastUpdated={ componentKey }
                      action={ product.action }
                      additionalOffersText={ product.additionalOffersText }
                      addToBagAction={ product.addToBagAction }
                      altImage={ product.altImage }
                      availability={ product.availability }
                      badge={ product.badge }
                      bookmarkAccessibility={ product.bookmarkAccessibility }
                      bookmarked={ product.bookmarked }
                      brandName={ product.brandName }
                      displayBookmark={ false }
                      hoverState={ false }
                      image={ product.image?.imageUrl }
                      kitPrice={ product.kitPrice }
                      listPrice={ product.listPrice }
                      productId={ product.productId }
                      productName={ product.productName }
                      promoText={ product.promoText }
                      rating={ product.rating }
                      reviewCount={ product.reviewCount }
                      salePrice={ product.salePrice }
                      skuId={ product.skuId }
                      sponsoredBadgeLabel={ product.sponsoredBadgeLabel }
                      variantLabel={ product.variantLabel }
                      viewOptionAction={ product.viewOptionAction }
                      salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
                      listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
                      salePriceRangeAccessibilityLabel={ salePriceRangeAccessibilityLabel }
                      listPriceRangeAccessibilityLabel={ listPriceRangeAccessibilityLabel }
                      { ...( reviewAccessibilityLabel && {
                        reviewAccessibilityLabel: reviewAccessibilityLabel
                      } ) }
                    />
                  </div>
                </Slide>
              );
            } )
          }
          { showLoader &&
            [...Array( loaderArraySize )].map( ( e, index ) => (
              <Slide key={ index }
                aria-hidden={ true }
                className='ProductRailHorizontal__Loader--show'
              >
                <Loader key={ index }
                  displayType='skeleton'
                  multipleLineLoader={ true }
                />
              </Slide>
            ) )
          }
        </Rail>
      </GridContainer>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} countLabel='' - The default value of the count label is empty string
 * @property {boolean} enableLoader=true - The default value for enableLoader is set to true
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string
 * @property {string} title='' - The default value of the title is empty string
 */
export const defaultProps = {
  countLabel: '',
  enableLoader: true,
  items: [],
  nextAccessibility: '',
  previousAccessibility: '',
  title: ''
};

/**
 * Property type definitions
 * @typedef ProductRailHorizontalProps
 * @type {object}
 * @property {string} carouselAccessibility - Value for aria label for product rail
 * @property {string} carouselCardAccessibility - Value for the product card aria label
 * @property {string} carouselDetailsAccessibility - Value for aria label for carousel
 * @property {number} componentKey - Sets the component key
 * @property {string} countLabel - Sets the count label for productRail list
 * @property {boolean} enableLoader - Sets enableLoader to true or false
 * @property {Array.<{promoText: string, reviewCount: Number, rating: Number, brandName: string, productName: string, variantLabel: string, badge: string, image: string, availability: array, addToBagAccessibility: string, additionalOffersText: string, altImage: string, bookmarkAccessibility: string, bookmarked: boolean, kitPrice: string, listPrice: string, salePrice: string, skuId: string}>} items - List of products to loop over and display in carousel
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label
 * @property {string} listPriceRangeAccessibilityLabel - Sets the list price range accessibility label
 * @property {boolean} loading - Sets the loading state to true or false
 * @property {string} nextAccessibility - Sets the aria label for next button
 * @property {object} nextClickAction - This is the action object for the Previous button
 * @property {string} previousAccessibility - Sets the aria label for previous button
 * @property {object} previousClickAction - This is the action object for the Next button
 * @property {string} reviewAccessibilityLabel - Value for the rating aria label
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label
 * @property {string} salePriceRangeAccessibilityLabel - Sets the sale price range accessibility label
 * @property {function} setShowSpacer - Sets the spacer below the component
 * @property {object} spotlight - This is the object for the spotlight
 * @property {string} subtitle - Sets the subtitle for productRail list
 * @property {string} title - Sets the title for productRail list
 * @property {object} viewAllAction - This is the action object with action label and url
 */
export const propTypes =  {
  /**
   * The prop that holds  aria label for carousel
   */
  carouselAccessibility: PropTypes.string,
  /**
   * The prop that holds  aria label for product card
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * The prop that holds  aria label for carousel
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * Sets the component key
  */
  componentKey: PropTypes.number,
  /**
  * The prop that holds the count label for the product list.
  */
  countLabel: PropTypes.string,
  /**
  * Sets enableLoader to true or false
  */
  enableLoader: PropTypes.bool,
  /**
  * The prop that holds the list of products to render.
  */
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      promoText: PropTypes.string,
      reviewCount: PropTypes.number,
      rating: PropTypes.number,
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantLabel: PropTypes.string,
      badge: PropTypes.string,
      image: PropTypes.object,
      availability: PropTypes.arrayOf( PropTypes.string ),
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      kitPrice: PropTypes.string,
      bookmarkAccessibility: PropTypes.string,
      addToBagAccessibility: PropTypes.string,
      additionalOffersText: PropTypes.string,
      altImage: PropTypes.string,
      bookmarked: PropTypes.bool,
      skuId: PropTypes.string
    } )
  ),
  /**
  * Sets the list price accessibility label
  */
  listPriceAccessibilityLabel: PropTypes.string,
  /**
  * Sets the list price range accessibility label
  */
  listPriceRangeAccessibilityLabel: PropTypes.string,
  /**
  * Sets the loading state to true or false
  */
  loading: PropTypes.bool,
  /**
  * The prop that holds the aria label for next button.
  */
  nextAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the next button.
   */
  nextClickAction: PropTypes.object,
  /**
  * The prop that holds the aria label for previous button.
  */
  previousAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the previous button.
   */
  previousClickAction: PropTypes.object,
  /**
  * The prop that decides to show aria-label.
  */
  reviewAccessibilityLabel: PropTypes.string,
  /**
  * Sets the sale price accessibility label
  */
  salePriceAccessibilityLabel: PropTypes.string,
  /**
  * Sets the sale price range accessibility label
  */
  salePriceRangeAccessibilityLabel: PropTypes.string,
  /**
  * Sets the spacer below the component
  */
  setShowSpacer: PropTypes.func,
  /**
   * This is the object for the spotlight
   */
  spotlight: PropTypes.object,
  /**
  * The prop that holds subtitle for the carousel.
  */
  subtitle: PropTypes.string,
  /**
  * The prop that holds title for the carousel.
  */
  title: PropTypes.string,
  /**
  * This is the action with label and url.
  */
  viewAllAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string

  } )
};

ProductRailHorizontal.propTypes = propTypes;
ProductRailHorizontal.defaultProps = defaultProps;

export default ProductRailHorizontal;